import { CarouselProvider, Dot } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { FC, useRef, useState } from 'react';
import './../../Games/Carousel/Carousel.scss';
import './ItvCarousel.scss';
import { useDeviceInfo, useBreakpointInfo, MediaBreakpoint } from '../../../hooks/useDeviceInfo';
import { joinStrings } from '../../../utils/string';
import { CarouselSliderProps, SliderWrapper } from '../../Games/Carousel';
import { ItvCarouselSlide, ItvCarouselSlideComponent } from './ItvCarouselSlide';

export type CarouselAlignment = 'center' | 'top' | 'bottom';
export type CarouselContentAlignment = 'center' | 'left' | 'right';
export type CarouselZoomBehavior = 'onClick' | 'onHover';
export type CarouselOffsetStyle = 'leftRight' | 'right';

export type ItvCarouselProps = {
  slides: ItvCarouselSlide[];
  autoplayInterval?: number;
  className?: string;
  alignment?: CarouselAlignment;
  contentAlignment?: CarouselContentAlignment;
  showDots?: boolean;
  showArrows?: boolean;
  infinite?: boolean;
  touchEnabled?: boolean;
  touchThreshold?: number;
  dragEnabled?: boolean;
  zoom?: boolean;
  zoomBehavior?: CarouselZoomBehavior;
  offsetPercentage?: number;
  offsetStyle?: CarouselOffsetStyle;
  step?: number;
  emptyTitle?: string;

  onClick?: (index: number) => void;
  onNext?: (index: number) => void;
  onBack?: (index: number) => void;

  customSlideWrapper?: FC<CarouselSliderProps>;
};

export const ItvCarousel: FC<ItvCarouselProps> = ({
  slides,
  autoplayInterval = 5000,
  className,
  alignment = 'center',
  contentAlignment = 'center',
  showDots,
  showArrows = false,
  infinite,
  touchEnabled = true,
  touchThreshold = 300,
  dragEnabled = false,
  zoom = false,
  zoomBehavior = 'onHover',
  offsetPercentage = 0,
  offsetStyle = 'right',
  step = 1,
  emptyTitle = 'Carousel Empty',
  onClick,
  onNext,
  onBack,
  customSlideWrapper,
}) => {
  const { isMobileDevice } = useDeviceInfo();
  const isMobile = useBreakpointInfo([MediaBreakpoint.MOBILE]);

  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const hasSlides = slides && slides.length > 0;

  if (!hasSlides) {
    return null;
  }

  const visibleSlides = 1;
  const totalSlides = slides.length;
  const hasMultipleSlides = totalSlides > 1;

  const shouldShowDots = showDots ?? hasMultipleSlides;
  const shouldUseInfinite = infinite ?? hasMultipleSlides;
  const shouldAutoplay = autoplayInterval > 0 && hasMultipleSlides;

  const slideNodes = slides.map((slide) => <ItvCarouselSlideComponent key={slide.id ?? slide.title} {...slide} />);

  const numberOfDots = Math.ceil(totalSlides / visibleSlides);
  const dots = Array.from({ length: numberOfDots }, (_, index) => index);

  const SliderWrapperComp: FC<CarouselSliderProps> = customSlideWrapper || SliderWrapper;

  const activeHandler = (state: boolean): void => {
    setActive(state);
  };

  return (
    <div className={joinStrings(['itv-carousel', className, `itv-carousel--${isMobile ? 'mobile' : 'desktop'}`])}>
      <div className="itv-carousel__carousel">
        <div
          data-testid="carousel-wrapper"
          className={joinStrings([
            'carousel-wrapper',
            active && 'carousel-wrapper--active',
            (isMobileDevice || touchEnabled) && 'carousel-wrapper--touchable',
            dragEnabled && 'carousel-wrapper--draggable',
            'carousel-wrapper--resizable',
          ])}
        >
          <CarouselProvider
            naturalSlideWidth={0}
            naturalSlideHeight={0}
            isPlaying={shouldAutoplay}
            interval={autoplayInterval}
            isIntrinsicHeight
            infinite={shouldUseInfinite}
            touchEnabled={isMobileDevice || touchEnabled}
            dragEnabled={isMobileDevice ? false : dragEnabled}
            visibleSlides={visibleSlides}
            totalSlides={totalSlides}
            step={step}
          >
            {totalSlides > 0 ? (
              <div ref={ref}>
                <SliderWrapperComp
                  visibleSlides={visibleSlides}
                  alignment={alignment}
                  contentAlignment={contentAlignment}
                  disabled={false}
                  showArrows={!isMobileDevice && showArrows && visibleSlides < totalSlides}
                  showDots={shouldShowDots}
                  isMobile={isMobileDevice}
                  offsetPercentage={offsetPercentage}
                  offsetStyle={offsetStyle}
                  touchThreshold={touchThreshold}
                  zoom={zoom}
                  zoomBehavior={zoomBehavior}
                  infinite={shouldUseInfinite}
                  slides={slideNodes}
                  onClick={onClick}
                  onNext={onNext}
                  onBack={onBack}
                  activeHandler={activeHandler}
                />
              </div>
            ) : (
              <div className="carousel-wrapper__empty" data-testid="carousel-empty">
                <p>{emptyTitle}</p>
              </div>
            )}

            {shouldShowDots && totalSlides > visibleSlides && (
              <div className="carousel-wrapper__dots" data-testid="carousel-dots">
                {dots.map((value) => (
                  <Dot data-testid="carousel-dot" key={value} slide={value * visibleSlides} />
                ))}
              </div>
            )}
          </CarouselProvider>
        </div>
      </div>
    </div>
  );
};
